import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  TextField,
  Alert,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import axios from "axios";
import { decryptText, decryptAppId } from "../../../utils/encryption";

const ParameterAllotment = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get parameters from URL
  const getParamFromQuery = (paramName, isAppId = false) => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedParam = searchParams.get(paramName);
    if (encryptedParam) {
      try {
        return isAppId ? decryptAppId(encryptedParam) : decryptText(encryptedParam);
      } catch (error) {
        return encryptedParam;
      }
    }
    return null;
  };

  const [appId, setAppId] = useState(getParamFromQuery("appId", true));
  const [assessmentId, setAssessmentId] = useState(getParamFromQuery("assessmentId"));
  const [assessorId, setAssessorId] = useState(getParamFromQuery("assessorId"));
  const [disciplineId, setDisciplineId] = useState(getParamFromQuery("disciplineId"));
  const [groupId, setGroupId] = useState(getParamFromQuery("groupId"));
  const [labTypeId, setLabTypeId] =  useState(getParamFromQuery("labTypeId"));

  const [disciplines, setDisciplines] = useState([]);
  const [groups, setGroups] = useState([]);
  const [subGroups, setSubGroups] = useState([]);
  const [scopeData, setScopeData] = useState([]);

  const [selectedDiscipline, setSelectedDiscipline] = useState("0");
  const [selectedGroup, setSelectedGroup] = useState("0");
  const [selectedSubGroup, setSelectedSubGroup] = useState("0");
  const [selectedAction, setSelectedAction] = useState("0"); // 0=Pending, 1=Alloted
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectAll, setSelectAll] = useState(false);

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Fetch disciplines when location changes
  useEffect(() => {
      fetchDisciplines();
  }, []);

  // Fetch groups when discipline changes
  useEffect(() => {
    if (selectedDiscipline !== "0") {
      fetchGroups();
    }
  }, [selectedDiscipline]);

  // Fetch sub groups when group changes (for Testing labs)
  useEffect(() => {
    if (labTypeId === '1' && selectedGroup !== "0") {
      fetchSubGroups();
    }
  }, [selectedGroup]);

  // Fetch scope data
  useEffect(() => {
      fetchScopeData();
  }, [selectedDiscipline, selectedGroup, selectedSubGroup, selectedAction, currentPage]);

  const fetchDisciplines = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/assessor/parameter-allotment/disciplines`,
        {
          params: {
            appId,
            orgId: localStorage.getItem("orgId") || "0",
            assessmentId: assessmentId,
            assessorId: assessorId,
            labTypeId: labTypeId,
          },
        }
      );
      if (response.data && response.data.data) {
        setDisciplines(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching disciplines:", error);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/assessor/parameter-allotment/groups`,
        {
          params: {
            appId,
            orgId: localStorage.getItem("orgId") || "0",
            assessmentId: assessmentId,
            assessorId: assessorId,
            labTypeId: labTypeId,
            disciplineId: selectedDiscipline,
          },
        }
      );
      if (response.data && response.data.data) {
        setGroups(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
    }
  };

  const fetchSubGroups = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/assessor/parameter-allotment/subgroups`,
        {
          params: {
            appId,
            orgId: localStorage.getItem("orgId") || "0",
            disciplineId: selectedDiscipline,
            groupId: selectedGroup,
          },
        }
      );
      if (response.data && response.data.data) {
        setSubGroups(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching sub groups:", error);
    }
  };

  const fetchScopeData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/assessor/parameter-allotment/scope`,
        {
          params: {
            appId,
            orgId: localStorage.getItem("orgId") || "0",
            disciplineId: selectedDiscipline,
            groupId: selectedGroup,
            subGroupId: selectedSubGroup,
            action: selectedAction,
            page: currentPage,
            pageSize: 100,
            assessmentId,
            assessorId,
            labTypeId,
          },
        }
      );
      if (response.data && response.data.data) {
        setScopeData(response.data.data);
        setTotalPages(response.data.totalPages || 1);
      } else {
        setScopeData([]);
      }
    } catch (error) {
      console.error("Error fetching scope data:", error);
      showSnackbar("Error fetching scope data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    const checked = event.target.checked;
    setSelectAll(checked);
    const updatedData = scopeData.map((item) => ({
      ...item,
      selected: checked,
    }));
    setScopeData(updatedData);
  };

  const handleRowSelect = (index) => {
    const updatedData = [...scopeData];
    updatedData[index].selected = !updatedData[index].selected;
    setScopeData(updatedData);
    setSelectAll(updatedData.every((item) => item.selected));
  };

  const handleAllotParameters = async () => {
    const selectedItems = scopeData.filter((item) => item.selected);
    
    if (selectedItems.length === 0) {
      showSnackbar("Please select at least one parameter", "warning");
      return;
    }

    setLoading(true);
    try {
      const promises = selectedItems.map((item) =>
        axios.post(
          `${process.env.REACT_APP_API_URL}/assessor/parameter-allotment/allot`,
          {
            appId,
            scopeId: item.id,
            labTypeId,
            orgId: localStorage.getItem("orgId") || "0",
            assessmentId,
            assessorId,
            mode: selectedAction === "0" ? 1 : 2, // 1=Allot, 2=Remove
          }
        )
      );

      await Promise.all(promises);
      
      showSnackbar(
        selectedAction === "0"
          ? "Parameters allotted successfully"
          : "Parameters removed successfully",
        "success"
      );
      
      // Refresh data
      setSelectAll(false);
      fetchScopeData();
    } catch (error) {
      console.error("Error allotting parameters:", error);
      showSnackbar("Error processing parameters", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const renderScopeTable = () => {
    if (labTypeId === '1') {
      // Testing Lab
      return (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell>
                  <Checkbox
                    checked={selectAll}
                    onChange={handleSelectAll}
                    disabled={scopeData.length === 0}
                  />
                </TableCell>
                <TableCell>S.No</TableCell>
                <TableCell>Discipline Name</TableCell>
                <TableCell>Group</TableCell>
                <TableCell>Sub Group</TableCell>
                <TableCell>Product/Material of test</TableCell>
                <TableCell>Specific Test Performed</TableCell>
                <TableCell>Test method</TableCell>
                <TableCell>Range of Testing</TableCell>
                <TableCell>(±)MU @Value</TableCell>
                <TableCell>Test Performed at</TableCell>
                <TableCell>Assessor Allotted</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scopeData.map((row, index) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Checkbox
                      checked={row.selected || false}
                      onChange={() => handleRowSelect(index)}
                    />
                  </TableCell>
                  <TableCell>{row.r || index + 1}</TableCell>
                  <TableCell>{row.disciplineName}</TableCell>
                  <TableCell>{row.GroupName}</TableCell>
                  <TableCell>{row.subGrpName}</TableCell>
                  <TableCell>{row.GroupTested}</TableCell>
                  <TableCell>{row.TypeOfTest}</TableCell>
                  <TableCell>{row.scopeyear}</TableCell>
                  <TableCell>{row.unit}</TableCell>
                  <TableCell>{row.measr}</TableCell>
                  <TableCell>{row.testPerformedAt}</TableCell>
                  <TableCell>{row.assessorName || ""}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    } else if (labTypeId === '2') {
      // Calibration Lab
      return (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell>
                  <Checkbox
                    checked={selectAll}
                    onChange={handleSelectAll}
                    disabled={scopeData.length === 0}
                  />
                </TableCell>
                <TableCell>S.No</TableCell>
                <TableCell>Discipline Name</TableCell>
                <TableCell>Group</TableCell>
                <TableCell>Equipment/Device</TableCell>
                <TableCell>Parameter</TableCell>
                <TableCell>Standard & Method</TableCell>
                <TableCell>Range</TableCell>
                <TableCell>CMC(±)</TableCell>
                <TableCell>Performed at</TableCell>
                <TableCell>Capability Mode</TableCell>
                <TableCell>Remarks</TableCell>
                <TableCell>Assessor Allotted</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scopeData.map((row, index) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Checkbox
                      checked={row.selected || false}
                      onChange={() => handleRowSelect(index)}
                    />
                  </TableCell>
                  <TableCell>{row.r || index + 1}</TableCell>
                  <TableCell>{row.disciplineName}</TableCell>
                  <TableCell>{row.GroupName}</TableCell>
                  <TableCell>{row.subGrpName}</TableCell>
                  <TableCell>{row.ScopeParameter}</TableCell>
                  <TableCell>{row.ScopeYear}</TableCell>
                  <TableCell>{row.unit}</TableCell>
                  <TableCell>{row.measr}</TableCell>
                  <TableCell>{row.testPerformedAt}</TableCell>
                  <TableCell>{row.CapabilityMode}</TableCell>
                  <TableCell>{row.rangeremark}</TableCell>
                  <TableCell>{row.assessorName || ""}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    } else if (labTypeId === '3') {
      // Medical Lab
      return (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell>
                  <Checkbox
                    checked={selectAll}
                    onChange={handleSelectAll}
                    disabled={scopeData.length === 0}
                  />
                </TableCell>
                <TableCell>S.No</TableCell>
                <TableCell>Discipline Name</TableCell>
                <TableCell>Type of Samples</TableCell>
                <TableCell>Specific Tests</TableCell>
                <TableCell>Standard</TableCell>
                <TableCell>Range</TableCell>
                <TableCell>%CV/MU(±)</TableCell>
                <TableCell>Performed at</TableCell>
                <TableCell>Assessor Allotted</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scopeData.map((row, index) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Checkbox
                      checked={row.selected || false}
                      onChange={() => handleRowSelect(index)}
                    />
                  </TableCell>
                  <TableCell>{row.r || index + 1}</TableCell>
                  <TableCell>{row.disciplineName}</TableCell>
                  <TableCell>{row.GroupTested}</TableCell>
                  <TableCell>{row.TypeOfTest}</TableCell>
                  <TableCell>{row.scopeyear}</TableCell>
                  <TableCell>{row.unit}</TableCell>
                  <TableCell>{row.measr}</TableCell>
                  <TableCell>{row.testPerformedAt}</TableCell>
                  <TableCell>{row.assessorName || ""}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    return null;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card elevation={3}>
        <CardContent>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Parameter Allotment - Scope of Accreditation
            </Typography>
          </Box>

          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>
                   Discipline
                </InputLabel>
                <Select
                  value={selectedDiscipline}
                  onChange={(e) => {
                    setSelectedDiscipline(e.target.value);
                    setCurrentPage(1);
                  }}
                  label="Discipline"
                >
                  <MenuItem value="0">--Select--</MenuItem>
                  {disciplines.map((disc) => (
                    <MenuItem key={disc.id} value={disc.id}>
                      {disc.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {(labTypeId === '1' || labTypeId === '2') && ( 
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>
                   Group
                </InputLabel>
                <Select
                  value={selectedGroup}
                  onChange={(e) => {
                    setSelectedGroup(e.target.value);
                    setCurrentPage(1);
                  }}
                  label="Group"
                >
                  <MenuItem value="0">--Select--</MenuItem>
                  {groups.map((grp) => (
                    <MenuItem key={grp.id} value={grp.id}>
                      {grp.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            )}
            {/* {labTypeId === '1' && (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sub Group</InputLabel>
                  <Select
                    value={selectedSubGroup}
                    onChange={(e) => {
                      setSelectedSubGroup(e.target.value);
                      setCurrentPage(1);
                    }}
                    label="Sub Group"
                  >
                    <MenuItem value="0">--Select--</MenuItem>
                    {subGroups.map((subGrp) => (
                      <MenuItem key={subGrp.id} value={subGrp.id}>
                        {subGrp.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )} */}

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Action</InputLabel>
                <Select
                  value={selectedAction}
                  onChange={(e) => {
                    setSelectedAction(e.target.value);
                    setCurrentPage(1);
                  }}
                  label="Action"
                >
                  <MenuItem value="0">Pending</MenuItem>
                  <MenuItem value="1">Allotted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Pagination */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2, gap: 2 }}>
              <Button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                Previous
              </Button>
              <TextField
                size="small"
                value={currentPage}
                onChange={(e) => {
                  const page = parseInt(e.target.value) || 1;
                  if (page > 0 && page <= totalPages) {
                    setCurrentPage(page);
                  }
                }}
                sx={{ width: 80 }}
              />
              <Typography sx={{ display: "flex", alignItems: "center" }}>
                / {totalPages}
              </Typography>
              <Button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              >
                Next
              </Button>
            </Box>

          {/* Action Buttons */}
          {scopeData.length > 0 && (
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleAllotParameters}
                disabled={loading}
              >
                {selectedAction === "0" ? "Allot Parameters" : "Remove Allotment"}
              </Button>
            </Box>
          )}

          {/* Scope Table */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : scopeData.length > 0 ? (
            renderScopeTable()
          ) : (
            <Alert severity="info">
              No parameters found. Please select filters to view scope data.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ParameterAllotment;
